/**
 * SupabaseAuthProvider — IAuthProvider implementation using Supabase Auth.
 *
 * Uses `@supabase/supabase-js` for session management, token refresh, and
 * social/email login. The Supabase SDK handles:
 * - Session persistence (localStorage by default)
 * - Automatic token refresh before expiry
 * - OAuth redirect flows (Google, Apple)
 *
 * The user-level Bearer token (`getUserToken()`) is the Supabase access_token
 * from the current session. Edge functions verify it via
 * `supabase.auth.getUser(token)`.
 *
 * Per-list tokens (getToken/setToken) remain for backwards compatibility
 * with anonymous password-protected lists.
 *
 * ADR: doc/adr/0002-auth-vendor-supabase-auth.md
 */

import { createClient, type SupabaseClient, type Session, type User } from '@supabase/supabase-js';
import type { IAuthProvider } from '../../interfaces/IAuthProvider';
import type { IStorage } from '../../interfaces/IStorage';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

type UnauthorizedListener = (listName: string) => void;

export class SupabaseAuthProvider implements IAuthProvider {
  private supabase: SupabaseClient;
  private storage: IStorage;
  private listeners: Set<UnauthorizedListener> = new Set();
  private currentSession: Session | null = null;
  private currentUser: User | null = null;
  // Set true when supabase-js detects a recovery token in the URL. The provider
  // subscribes in its constructor (at module load, before React mounts), so it
  // catches the PASSWORD_RECOVERY event even if it fires before App's listener.
  private recoveryRequested = false;

  constructor(options: { storage: IStorage }) {
    this.storage = options.storage;

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('SupabaseAuthProvider: Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
    }

    this.supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true, // Handle OAuth redirect callbacks
      },
    });

    // Listen for auth state changes
    this.supabase.auth.onAuthStateChange((event, session) => {
      this.currentSession = session;
      this.currentUser = session?.user ?? null;
      if (event === 'PASSWORD_RECOVERY') {
        this.recoveryRequested = true;
      }
    });

    // Initialize session from storage
    this.supabase.auth.getSession().then(({ data: { session } }) => {
      this.currentSession = session;
      this.currentUser = session?.user ?? null;
    });
  }

  // --- Per-list token methods (backwards compat for anonymous lists) ---

  getToken(listName: string): string | null {
    return this.storage.get(`shopman_token_${listName}`);
  }

  setToken(listName: string, token: string): void {
    this.storage.set(`shopman_token_${listName}`, token);
  }

  clearToken(listName: string): void {
    this.storage.remove(`shopman_token_${listName}`);
  }

  async getFreshToken(listName: string): Promise<string | null> {
    // Per-list tokens don't refresh — return cached
    return this.getToken(listName);
  }

  // --- Unauthorized handling ---

  onUnauthorized(handler: UnauthorizedListener): () => void {
    this.listeners.add(handler);
    return () => { this.listeners.delete(handler); };
  }

  notifyUnauthorized(listName: string): void {
    for (const listener of this.listeners) {
      listener(listName);
    }
  }

  // --- User-level auth (Supabase sessions) ---

  getUserToken(): string | null {
    return this.currentSession?.access_token ?? null;
  }

  async getFreshUserToken(): Promise<string | null> {
    // getSession() returnerer det cachede token UDEN netværkskald hvis det
    // stadig er gyldigt, men refresher via refresh_token'et hvis access_token
    // er udløbet (inden for EXPIRY_MARGIN). Kritisk på native/Capacitor hvor
    // auto-refresh-timeren er suspenderet mens appen er i baggrunden — det
    // cachede token kan derfor være udløbet ved resume.
    try {
      const { data, error } = await this.supabase.auth.getSession();
      if (error) {
        // Refresh fejlede (fx udløbet refresh_token) — fald tilbage til det
        // cachede token; edge-funktionen afviser det hvis det er ugyldigt, og
        // 401-handleren prompter re-login.
        return this.currentSession?.access_token ?? null;
      }
      if (data.session) {
        this.currentSession = data.session;
        this.currentUser = data.session.user;
      }
      return data.session?.access_token ?? null;
    } catch {
      return this.currentSession?.access_token ?? null;
    }
  }

  getUserId(): string | null {
    return this.currentUser?.id ?? null;
  }

  // --- Supabase Auth methods (exposed for login UI) ---

  /** Get the underlying Supabase client (for auth UI). */
  getClient(): SupabaseClient {
    return this.supabase;
  }

  /** Get the current authenticated user. */
  getUser(): User | null {
    return this.currentUser;
  }

  /** Check if user is signed in. */
  isSignedIn(): boolean {
    return this.currentSession !== null;
  }

  /** True if a password-recovery token was detected in the URL (one-shot read). */
  consumeRecoveryRequest(): boolean {
    const v = this.recoveryRequested;
    this.recoveryRequested = false;
    return v;
  }

  /** Sign up with email + password. */
  async signUp(email: string, password: string) {
    const { data, error } = await this.supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  }

  /** Sign in with email + password. */
  async signIn(email: string, password: string) {
    const { data, error } = await this.supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }

  /** Sign in with OAuth provider (Google, Apple). */
  async signInWithOAuth(provider: 'google' | 'apple') {
    const { data, error } = await this.supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin + window.location.pathname,
      },
    });
    if (error) throw error;
    return data;
  }

  /** Sign out. */
  async signOut() {
    const { error } = await this.supabase.auth.signOut();
    if (error) throw error;
    this.currentSession = null;
    this.currentUser = null;
  }

  /** Send password reset email. */
  async resetPassword(email: string) {
    // redirectTo points at the app root (NOT a hash route) so the recovery
    // token lands in a clean URL hash that supabase-js can parse. The app
    // listens for the PASSWORD_RECOVERY event and shows the reset UI as a
    // router-independent override (see App.tsx).
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw error;
  }
}
